import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Squares2X2Icon,
    CheckCircleIcon,
    EyeIcon,
    CheckIcon,
    LockClosedIcon,
    QueueListIcon,
    StopIcon,
    ArrowUpIcon,
    CalendarIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import PhotoLightbox from './PhotoLightbox';
import { MatureCover, MATURE_BLUR, useMatureConsent } from './MatureCover';
import SelectionTray from './SelectionTray';
import Loading from '../Loading';
import AuthModal from '../auth/AuthModal';
import DownloadEmailModal from '../DownloadEmailModal';
import { SearchModal } from './SearchModal';
import { PhotoMap } from './PhotoMap';
import { cn } from '../ui/utils';
import { NoirDateRangePicker } from '../ui/NoirDateRangePicker';
import { setGalleryPass, withGalleryPass } from '../../lib/galleryPass';
import { buyerTotal, galleryTotal, type BuyerPricing } from '../../lib/buyerPricing';
import SaveToPhotosSheet, { canSaveToPhotos, photoFilename, type SaveItem } from './SaveToPhotosSheet';

// Gallery photos are loaded a page at a time to keep database egress low —
// pulling the entire library on every (often bot-driven) page view was the
// cause of a large Supabase egress overage. Users load more on demand.
const GALLERY_PAGE_SIZE = 150;
const LIVE_POLL_MS = 10000;

// A gallery holds video as well as stills. Everything below keys off this one
// field, so a row that predates it (null) still renders as an image exactly as
// it always did.
export function isVideoPhoto(photo: { mime_type?: string | null }): boolean {
    return Boolean(photo.mime_type?.startsWith('video/'));
}

interface Photo {
    id: string;
    title: string;
    thumbnail_url: string;
    google_drive_file_id: string;
    mime_type?: string | null;
    created_at: string;
    updated_at?: string;
    price?: number;
    library_id: string;
    /** Nudity: blurred behind an 18+ confirmation (MatureCover). */
    is_mature?: boolean | null;
    metadata?: {
        camera_make?: string;
        camera_model?: string;
        iso?: number;
        focal_length?: number;
        aperture?: number;
        shutter_speed?: number;
        date_taken?: string;
        time?: string;
        copyright?: string;
        [key: string]: any;
    };
}

interface PhotoLibrary {
    id: string;
    name: string;
    slug: string;
    description: string;
    price: number;
    // Whole album, for every buyer (owner, 2026-11-02). Checkout charges the same.
    album_price?: number | null;
    is_private?: boolean;
    owner_id?: string;
    google_drive_folder_id?: string;
    gdrive_folder_id?: string;
    // On during an event: photos the owner publishes from /admin/live appear
    // here without a reload.
    live_mode?: boolean;
    // Set on client work shot by a subcontractor. When `photographer` is
    // present the gallery renders a byline under the description; the
    // Instagram URL, when present, makes that byline a link to them.
    metadata?: {
        photographer?: string;
        photographer_handle?: string;
        photographer_instagram?: string;
    } | null;
}

export interface PricingOption {
    id: string;
    name: string;
    price: number;
    photo_count: number;
}

const PhotoCard: React.FC<{
    photo: Photo;
    index: number;
    isSelected: boolean;
    isPurchased: boolean;
    // Zero-priced gallery: unlocked for everyone, no purchase needed.
    isFree?: boolean;
    activeTab: string;
    singlePrice: number;
    viewMode: 'grid' | 'single';
    onToggleSelect: () => void;
    onLightbox: () => void;
    onDownload?: () => void;
    /** This viewer has downloaded/saved this photo before. */
    isDownloaded?: boolean;
}> = ({ photo, index, isSelected, isPurchased, isFree = false, activeTab, singlePrice, viewMode, onToggleSelect, onLightbox, onDownload, isDownloaded = false }) => {
    const [rotation, setRotation] = useState(0);
    const isUnlocked = isPurchased || isFree;
    const [matureOk, confirmMature] = useMatureConsent();
    const hideMature = Boolean(photo.is_mature) && !matureOk;

    // Dynamic styles based on view mode (Grid vs Single)
    const isSingle = viewMode === 'single';

    const handleFlip = (e: React.MouseEvent) => {
        e.stopPropagation();
        setRotation(prev => prev + 180);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`relative bg-black group perspective-1000 ${isSingle ? 'w-full' : 'aspect-square'}`}
            style={{ perspective: '1000px' }}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div
                className={`w-full relative transition-transform duration-700 ${isSingle ? 'h-auto' : 'h-full'}`}
                style={{
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d',
                    transform: `rotateY(${rotation}deg)`
                }}
            >
                {/* FRONT SIDE */}
                <div
                    className={`${isSingle ? 'relative w-full h-auto' : 'absolute inset-0 w-full h-full'} backface-hidden flex items-center justify-center bg-black cursor-pointer`}
                    style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', zIndex: rotation % 360 === 0 ? 2 : 1 }}
                    onClick={handleFlip}
                >
                    {/* Wrapper to constrain overlays to image bounds */}
                    <div className={`relative overflow-hidden ${isSingle ? 'w-auto h-auto' : 'w-full h-full'}`}>
                        {hideMature && <MatureCover onConfirm={confirmMature} />}
                        {/* Protection Layer - Intercepts all touch/click events to prevent saving */}
                        {!isUnlocked && (
                            <div
                                className="absolute inset-0 z-20 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); onLightbox(); }}
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                    WebkitTouchCallout: 'none',
                                    WebkitUserSelect: 'none',
                                    userSelect: 'none'
                                }}
                            />
                        )}
                        {isVideoPhoto(photo) ? (
                            // preload="metadata" so the grid does not pull tens of
                            // megabytes per clip on first paint; the poster frame
                            // comes from the stream endpoint's image path.
                            <video
                                src={`/api/gallery/stream?fileId=${photo.google_drive_file_id}`}
                                controls={isUnlocked}
                                playsInline
                                preload="metadata"
                                onClick={(e) => { e.stopPropagation(); if (isUnlocked) onLightbox(); }}
                                className={`${isSingle ? 'max-w-full w-auto h-auto max-h-[85vh] md:max-h-[calc(100vh-280px)] object-contain' : 'w-full h-full object-contain'} select-none transition-all duration-500 ${!isUnlocked ? 'pointer-events-none' : 'cursor-pointer'} ${hideMature ? MATURE_BLUR : ''}`}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        ) : (
                            <img
                                src={`/api/gallery/stream?fileId=${photo.google_drive_file_id}&size=1200`}
                                alt={photo.title}
                                onClick={(e) => { e.stopPropagation(); if (isUnlocked) onLightbox(); }}
                                className={`${isSingle ? 'max-w-full w-auto h-auto max-h-[85vh] md:max-h-[calc(100vh-280px)] object-contain' : 'w-full h-full object-contain'} select-none transition-all duration-500 ${!isUnlocked ? 'pointer-events-none' : 'cursor-pointer'} ${hideMature ? MATURE_BLUR : ''}`}
                                draggable={false}
                                loading="lazy"
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                    WebkitTouchCallout: 'none',
                                    WebkitUserSelect: 'none',
                                    userSelect: 'none'
                                }}
                            />
                        )}

                        {/* Brand overlay (preview protection) */}
                        {!isUnlocked && (
                            <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center overflow-hidden">
                                <img src="/logo.png" alt="" className="w-1/2 opacity-[0.09] brightness-0 invert select-none object-contain" />
                            </div>
                        )}

                        {/* Meta Flip Button */}
                        <button
                            onClick={handleFlip}
                            className="absolute bottom-2 right-2 z-[45] outline-none"
                            title="View Metadata"
                        >
                            <div className={`w-7 h-7 md:w-8 md:h-8 bg-black/40 backdrop-blur-md rounded-full text-white flex items-center justify-center hover:bg-black/60 transition-all ${isSingle ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}>
                                <EyeIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                            </div>
                        </button>

                        {/* Selection Checkbox (Front) */}
                        {activeTab === 'storefront' && !isPurchased && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
                                className="absolute top-2 right-2 z-[45] outline-none"
                            >
                                <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all ${isSelected
                                    ? 'bg-white scale-110'
                                    : 'bg-black/40 backdrop-blur-md hover:bg-black/60'
                                    }`}>
                                    {isSelected && <CheckIcon className="w-3.5 h-3.5 md:w-5 md:h-5 text-black stroke-[3]" />}
                                </div>
                            </button>
                        )}

                        {/* Direct download — purchased photos and every photo in a free gallery */}
                        {isUnlocked && onDownload && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDownload(); }}
                                className="absolute bottom-2 left-2 z-[45] outline-none"
                                title="Download"
                                aria-label="Download"
                            >
                                <div className={`w-7 h-7 md:w-8 md:h-8 bg-black/40 backdrop-blur-md rounded-full text-white flex items-center justify-center hover:bg-black/60 transition-all ${isSingle ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}>
                                    <ArrowDownTrayIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                </div>
                            </button>
                        )}

                        {/* Downloaded by this viewer */}
                        {isDownloaded && (
                            <div
                                className="absolute bottom-1.5 right-1.5 z-[45] flex items-center gap-1 bg-white text-black px-1.5 py-0.5 text-[7px] md:text-[8px] font-black uppercase tracking-[0.1em]"
                                title="You've downloaded this"
                                data-testid="downloaded-badge"
                            >
                                <ArrowDownTrayIcon className="w-2.5 h-2.5" />
                                SAVED
                            </div>
                        )}

                        {/* Purchased Badge */}
                        {isPurchased && (
                            <div className="absolute top-1.5 left-1.5 z-[45] flex items-center gap-1 bg-white text-black px-1.5 py-0.5 text-[7px] md:text-[8px] font-black uppercase tracking-[0.1em]">
                                <CheckCircleIcon className="w-2.5 h-2.5" />
                                PROPRIETARY
                            </div>
                        )}
                    </div>
                </div>

                {/* BACK SIDE (METADATA) */}
                <div
                    className="absolute inset-0 w-full h-full backface-hidden bg-black flex flex-col justify-between cursor-pointer"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        zIndex: rotation % 360 === 180 ? 2 : 1,
                        padding: isSingle ? '1.25rem' : '1.5vw'
                    }}
                    onClick={handleFlip}
                >
                    <div className={isSingle ? 'space-y-4 md:space-y-8' : 'space-y-[0.5vw] md:space-y-4'}>
                        <div className="space-y-[0.2vw] md:space-y-1">
                            <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Camera</p>
                            <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono uppercase leading-tight truncate`}>
                                {photo.metadata?.cameraMake || photo.metadata?.camera_make || ''} {photo.metadata?.cameraModel || photo.metadata?.camera_model || 'Unknown'}
                            </p>
                        </div>
                        <div className={`grid grid-cols-2 ${isSingle ? 'gap-y-4 gap-x-4 md:gap-x-12' : 'gap-y-[0.5vw] gap-x-[1vw] md:gap-x-4'}`}>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>ISO</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono leading-tight`}>{photo.metadata?.isoSpeed || photo.metadata?.iso || '-'}</p>
                            </div>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Aperture</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono leading-tight`}>f/{photo.metadata?.aperture || '-'}</p>
                            </div>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Focal</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono leading-tight`}>{(photo.metadata?.focalLength || photo.metadata?.focal_length) ? `${photo.metadata?.focalLength || photo.metadata?.focal_length}mm` : '-'}</p>
                            </div>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Shutter</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono leading-tight`}>
                                    {(() => {
                                        const shutter = photo.metadata?.exposureTime || photo.metadata?.shutter_speed;
                                        if (!shutter) return '-';
                                        return Number(shutter) < 1
                                            ? `1/${Math.round(1 / Number(shutter))}s`
                                            : `${shutter}s`;
                                    })()}
                                </p>
                            </div>
                        </div>
                        <div className={`grid grid-cols-2 ${isSingle ? 'gap-y-4 gap-x-4 md:gap-x-12' : 'gap-y-[0.5vw] gap-x-[1vw] md:gap-x-4'}`}>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Date Taken</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono uppercase leading-tight`}>
                                    {(() => {
                                        const dateVal = photo.metadata?.time || photo.metadata?.date_taken;
                                        return dateVal ? new Date(dateVal).toLocaleDateString() : 'Unknown';
                                    })()}
                                </p>
                            </div>
                            <div>
                                <p className={`${isSingle ? 'text-[9px] md:text-base mb-1' : 'text-[1.4vw] md:text-xs mb-[0.1vw]'} text-white/50 uppercase tracking-widest font-bold leading-none`}>Location</p>
                                <p className={`${isSingle ? 'text-sm md:text-2xl' : 'text-[1.8vw] md:text-sm'} text-white font-mono uppercase leading-tight truncate whitespace-nowrap`}>
                                    {photo.metadata?.city || 'Austin, TX'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-1 md:pt-4">
                        <p className={`${isSingle ? 'text-[8px] md:text-sm' : 'text-[1.2vw] md:text-[10px]'} text-white font-medium tracking-widest uppercase mb-[0.1vw] md:mb-2 text-center w-full`}>
                            © {new Date(photo.metadata?.date_taken || photo.created_at).getFullYear()} {photo.metadata?.copyright || 'THE LOST+UNFOUNDS'}
                        </p>
                        <p className={`${isSingle ? 'text-[7px] md:text-xs' : 'text-[1vw] md:text-[8px]'} text-white/50 uppercase tracking-wider text-center font-bold`}>
                            TAP TO FLIP
                        </p>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const PhotoGallery: React.FC<{ librarySlug: string; inline?: boolean }> = ({ librarySlug, inline = false }) => {
    const { user, loading: authLoading } = useAuth();
    const [library, setLibrary] = useState<PhotoLibrary | null>(null);
    const [rawPricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
    // The signed-in viewer's role here (GET /api/gallery/pass): an album owner
    // has every photo free; an à la carte buyer sees the whole album behind the
    // watermark overlay and buys photos at buyerPrice each (owner, 2026-11-02).
    const [galleryRole, setGalleryRole] = useState<{ role: 'owner' | 'buyer'; buyerPrice: number | null; pricing: BuyerPricing | null } | null>(null);
    // À la carte buyers see their own bundles (e.g. 4 for $10), not the album's.
    const pricingOptions: PricingOption[] = galleryRole?.role === 'buyer'
        ? (galleryRole.pricing?.bundles || []).map((b, i) => ({ id: `buyer-${i}`, name: `${b.photo_count} photos`, price: b.price, photo_count: b.photo_count } as PricingOption))
        : rawPricingOptions;
    // Set by BUY WHOLE ALBUM: the server then charges for every photo in it.
    const wholeAlbumRef = useRef(false);
    const [photos, setPhotos] = useState<Photo[]>([]);
    // Every photo in the album, not just the pages loaded so far — the count
    // checkout prices "the whole album" against.
    const [albumTotal, setAlbumTotal] = useState(0);
    // Whole-album offer: an à la carte buyer's own price (falls back to the
    // gallery's on the server), else the gallery's album_price for everyone.
    const albumOffer: { price: number; count: number } | null = galleryRole?.role === 'buyer'
        ? (galleryRole.pricing?.albumPrice ? { price: Number(galleryRole.pricing.albumPrice), count: galleryRole.pricing.albumCount } : null)
        : (Number(library?.album_price) > 0 && albumTotal > 0 ? { price: Number(library!.album_price), count: albumTotal } : null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [purchasedPhotos, setPurchasedPhotos] = useState<Photo[]>([]);
    const [selectedPhotos, setSelectedPhotos] = useState<Photo[]>([]);
    const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
    const [mapActivePhoto, setMapActivePhoto] = useState<Photo | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'storefront' | 'assets'>('storefront');
    const [showBackToTop, setShowBackToTop] = useState(false);
    const isAllPublic = librarySlug === 'all-public';
    const [viewMode, setViewMode] = useState<'grid' | 'single' | 'map'>('grid');
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    // Floating console tray — only one card open at a time (bento-design "Platform Console Tray")
    const [openCard, setOpenCard] = useState<'filter' | null>(null);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authMessage, setAuthMessage] = useState<string | undefined>(undefined);
    const [authTitle, setAuthTitle] = useState<string | undefined>(undefined);

    // Buyer email (used for order receipts + entitlement lookup)
    const [buyerEmail, setBuyerEmail] = useState<string | null>(null);
    // On a phone, downloads go to the camera roll through the share sheet.
    const [saveSheet, setSaveSheet] = useState<SaveItem[] | null>(null);

    // Photos this viewer has downloaded, marked SAVED on the grid. Signed in:
    // from the download log for their verified email (follows them across
    // devices). Not signed in: remembered on this device only — an email typed
    // into the download prompt proves nothing, so the server won't answer for it.
    const downloadedKey = `gallery_downloaded_${librarySlug}`;
    const [downloadedIds, setDownloadedIds] = useState<Set<string>>(() => {
        try { return new Set(JSON.parse(localStorage.getItem(`gallery_downloaded_${librarySlug}`) || '[]')); } catch { return new Set(); }
    });
    const markDownloaded = (ids: string[]) => {
        if (!ids.length) return;
        setDownloadedIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => next.add(id));
            try { localStorage.setItem(downloadedKey, JSON.stringify([...next])); } catch { /* private mode */ }
            return next;
        });
    };
    useEffect(() => {
        let cancelled = false;
        setGalleryPass(librarySlug, null);
        setGalleryRole(null);
        if (!user) return;
        (async () => {
            try {
                const res = await fetch(`/api/gallery/pass?slug=${encodeURIComponent(librarySlug)}`);
                const data = res.ok ? await res.json() : null;
                if (cancelled || !data?.role || !data.pass) return;
                setGalleryPass(librarySlug, data.pass);
                setGalleryRole({ role: data.role, buyerPrice: data.buyerPrice ?? null, pricing: data.pricing ?? null });
            } catch { /* no pass: downloads of a private album stay locked */ }
        })();
        return () => { cancelled = true; };
    }, [librarySlug, user?.id]);

    useEffect(() => {
        let cancelled = false;
        try { setDownloadedIds(new Set(JSON.parse(localStorage.getItem(downloadedKey) || '[]'))); } catch { setDownloadedIds(new Set()); }
        if (!user) return;
        (async () => {
            try {
                const res = await fetch(`/api/gallery/my-downloads?slug=${encodeURIComponent(librarySlug)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && Array.isArray(data.photoIds)) markDownloaded(data.photoIds);
            } catch { /* marks are a convenience; never block the gallery */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [librarySlug, user?.id]);

    // Drive-native gallery: true when the API returned 403 (not in invited_emails)
    const [driveAccessDenied, setDriveAccessDenied] = useState(false);

    // Checkout modal state — picks BTC vs Fiat after email is collected
    const [paymentPickerOpen, setPaymentPickerOpen] = useState(false);
    const [checkoutPending, setCheckoutPending] = useState(false);

    // Lightning payment state (BTC path)
    const [lightningInvoice, setLightningInvoice] = useState<string | null>(null);
    const [lightningAmount, setLightningAmount] = useState<number>(0);
    const [lightningModalOpen, setLightningModalOpen] = useState(false);


    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [showCalendar, setShowCalendar] = useState(false);

    // Tag filter state
    const [availableTags, setAvailableTags] = useState<{ id: string; name: string; type: string }[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    // Map of photo_id → set of tag_ids it belongs to
    const [photoTagsMap, setPhotoTagsMap] = useState<Map<string, Set<string>>>(new Map());
    const photosRef = useRef<HTMLDivElement>(null);
    // Pagination: track the library ids and how many rows we've pulled so
    // "Load More" can request the next page via .range().
    const libraryIdsRef = useRef<string[]>([]);
    const offsetRef = useRef(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const storageKey = `gallery_selection_${librarySlug}`;

    useEffect(() => {
        const controller = new AbortController();
        fetchGallery(controller.signal);

        // Load saved selections from localStorage
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setSelectedPhotos(parsed);
            } catch (err) {
                console.error('Error parsing saved selections:', err);
            }
        }

        // Restore buyer email from past purchases / downloads
        const savedEmail =
            localStorage.getItem('tlau_download_email') ||
            localStorage.getItem('credit_email');
        if (savedEmail) {
            setBuyerEmail(savedEmail);
        }

        return () => controller.abort();
    }, [librarySlug]);


    // Persist selections to localStorage
    useEffect(() => {
        if (selectedPhotos.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(selectedPhotos));
        } else {
            localStorage.removeItem(storageKey);
        }
    }, [selectedPhotos, storageKey]);

    // Track scroll position to show/hide back to top button
    useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 500);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ⌘K / Ctrl+K opens search modal
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchModalOpen(v => !v);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    // Check for purchased assets if user is logged in
    useEffect(() => {
        const controller = new AbortController();
        if (user?.email && library) {
            fetchPurchasedAssets(controller.signal);
        }
        return () => controller.abort();
    }, [user, library]);

    // Live event: pick up photos as the owner publishes them.
    //
    // Keyed on updated_at, not created_at. created_at is the capture time, and
    // a frame shot a minute ago can be published after one shot just now — a
    // created_at cursor would skip it. updated_at is stamped by the database
    // when the row is written, so it only moves forward. The cursor is always
    // a server timestamp read back from a row, never this device's clock.
    useEffect(() => {
        if (!library?.live_mode || isAllPublic || loading) return;
        const libraryId = library.id;
        let cursor: string | null = null;
        let stopped = false;

        const baseline = async () => {
            const { data } = await supabase
                .from('photos')
                .select('updated_at')
                .eq('library_id', libraryId)
                .order('updated_at', { ascending: false })
                .limit(1);
            cursor = data?.[0]?.updated_at ?? new Date(0).toISOString();
        };
        const poll = async () => {
            if (stopped || cursor === null || document.visibilityState !== 'visible') return;
            const { data, error } = await supabase
                .from('photos')
                .select('*')
                .eq('library_id', libraryId)
                .gt('updated_at', cursor)
                .order('updated_at', { ascending: true })
                .limit(100);
            if (stopped || error || !data || data.length === 0) return;
            cursor = (data[data.length - 1] as Photo).updated_at || cursor;
            setPhotos(prev => {
                const key = (p: Photo) => p.google_drive_file_id || p.id;
                const incoming = new Map((data as Photo[]).map(p => [key(p), p]));
                // A photo already on screen can come back marked 18+ (just after
                // upload, or by the admin): carry the flag over so it blurs.
                let changed = false;
                const current = prev.map(p => {
                    const next = incoming.get(key(p));
                    if (!next || Boolean(next.is_mature) === Boolean(p.is_mature)) return p;
                    changed = true;
                    return { ...p, is_mature: next.is_mature };
                });
                const have = new Set(prev.map(key));
                const fresh = (data as Photo[]).filter(p => !have.has(key(p)));
                // "Load more" pages by offset, so these rows shift its next page
                // down by a few. It drops rows it already has, so the overlap
                // costs a few duplicate fetches and never a missed photo.
                if (fresh.length === 0) return changed ? current : prev;
                return [...fresh.reverse(), ...current];
            });
        };

        baseline();
        const timer = window.setInterval(poll, LIVE_POLL_MS);
        return () => {
            stopped = true;
            window.clearInterval(timer);
        };
    }, [library?.id, library?.live_mode, isAllPublic, loading]);

    // For private galleries with a Drive folder but no Supabase photos, load directly from Drive
    useEffect(() => {
        if (!library) return;
        if (!library.is_private) return;
        if (loading) return;
        if (photos.length > 0) return;
        const folderId = library.google_drive_folder_id || library.gdrive_folder_id;
        if (!folderId) return;
        fetchDrivePhotos(library.slug);
    }, [library, loading]);

    // A private gallery with nobody signed in goes to its access page, where an
    // invited client gets a sign-in link by email. Never the site's password
    // modal: clients have no password, and that modal was a dead end (Bandit,
    // 2026-11-02, re-tapping a used one-time link at midnight). Waits for the
    // session to finish loading — right after a sign-in link the tokens are
    // still being read from the URL, and leaving then would throw them away.
    useEffect(() => {
        if (loading || authLoading) return;
        if (!library?.is_private) return;
        if (user) return;
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        if (/access_token=/.test(hash)) return;
        const expired = /error_code=otp_expired|error=access_denied/.test(hash);
        window.location.replace(`/gallery/${encodeURIComponent(library.slug)}/access${expired ? '?expired=1' : ''}`);
    }, [library, user, loading, authLoading]);

    async function fetchPurchasedAssets(signal?: AbortSignal) {
        // Check for logged in user OR guest email
        const userEmail = user?.email || localStorage.getItem('credit_email');
        if (!library || !userEmail) return;

        try {
            const { data, error } = await supabase
                .from('photo_entitlements')
                .select('photo_id, photos (*), photo_orders!inner(email)')
                .eq('photo_orders.email', userEmail);

            if (error) {
                if (error.message === 'Fetch is aborted' || error.code === '20') return;
                throw error;
            }

            // Filter photos that belong to this library (or all photos in all-public mode)
            const photosLoadedIds = new Set(photos.map(p => p.id));
            const filtered = data
                ?.map(ent => Array.isArray(ent.photos) ? ent.photos[0] : ent.photos)
                ?.filter(p => p && (isAllPublic ? photosLoadedIds.has(p.id) : p.library_id === library.id)) as any as Photo[] || [];

            setPurchasedPhotos(filtered);
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching purchased assets:', err);
        }
    }

    async function fetchDrivePhotos(slug: string) {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            if (!token) {
                setDriveAccessDenied(true);
                return;
            }
            const res = await fetch(`/api/gallery/drive-list?slug=${encodeURIComponent(slug)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 403) {
                setDriveAccessDenied(true);
                return;
            }
            if (!res.ok) return;
            const data = await res.json();
            if (Array.isArray(data.photos) && data.photos.length > 0) {
                setPhotos(data.photos);
            }
        } catch (err) {
            console.error('[drive-list] fetch error:', err);
        }
    }

    async function fetchGallery(signal?: AbortSignal) {
        try {
            setLoading(true);

            let libraryIds: string[];

            if (isAllPublic) {
                // Aggregate mode: load every public library's photos in one view
                const { data: pubLibs, error: pubLibsError } = await supabase
                    .from('photo_libraries')
                    .select('id, name, slug, description, is_private')
                    .eq('is_private', false)
                    .eq('published', true);

                if (pubLibsError) {
                    if (pubLibsError.message === 'Fetch is aborted' || pubLibsError.code === '20') return;
                    throw pubLibsError;
                }

                libraryIds = (pubLibs || []).map(l => l.id);

                setLibrary({
                    id: 'all-public',
                    name: 'THE GALLERY',
                    slug: 'all-public',
                    description: 'Free public photos — organized by album.',
                    price: 0,
                    is_private: false,
                });
                setPricingOptions([]);
            } else {
                const { data: libData, error: libError } = await supabase
                    .from('photo_libraries')
                    .select('*')
                    .eq('slug', librarySlug)
                    .single();

                if (libError) {
                    if (libError.message === 'Fetch is aborted' || libError.code === '20') return;
                    throw libError;
                }
                setLibrary(libData);
                libraryIds = [libData.id];

                // Fetch pricing options
                const { data: pricingData } = await supabase
                    .from('gallery_pricing_options')
                    .select('*')
                    .eq('library_id', libData.id)
                    .eq('is_active', true)
                    .order('photo_count', { ascending: true });

                setPricingOptions(pricingData || []);
            }

            if (libraryIds.length === 0) {
                setPhotos([]);
                return;
            }

            libraryIdsRef.current = libraryIds;
            const { data: photoData, error: photoError, count: photoTotal } = await supabase
                .from('photos')
                .select('*', { count: 'exact' })
                .in('library_id', libraryIds)
                .order('created_at', { ascending: false })
                .order('title', { ascending: false })
                .range(0, GALLERY_PAGE_SIZE - 1);

            if (photoError) {
                if (photoError.message === 'Fetch is aborted' || photoError.code === '20') return;
                throw photoError;
            }
            // Deduplicate by google_drive_file_id in case the DB constraint is not yet applied
            const seen = new Set<string>();
            const fetchedPhotos = (photoData || []).filter((p: Photo) => {
                const key = p.google_drive_file_id || p.id;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setPhotos(fetchedPhotos);
            setAlbumTotal(photoTotal ?? fetchedPhotos.length);
            offsetRef.current = (photoData || []).length;
            setHasMore((photoData || []).length === GALLERY_PAGE_SIZE);

            // Fetch tags for photos in this gallery (chunk by 200 to stay under URL length limits)
            if (fetchedPhotos.length > 0) {
                const photoIds = fetchedPhotos.map((p: Photo) => p.id);
                const tagMap = new Map<string, Set<string>>();
                const tagIndex = new Map<string, { id: string; name: string; type: string }>();

                const chunkSize = 200;
                for (let i = 0; i < photoIds.length; i += chunkSize) {
                    const chunk = photoIds.slice(i, i + chunkSize);
                    const { data: ptData } = await supabase
                        .from('photo_tags')
                        .select('photo_id, tag_id, tags(id, name, type)')
                        .in('photo_id', chunk);

                    for (const row of (ptData || []) as any[]) {
                        if (!tagMap.has(row.photo_id)) tagMap.set(row.photo_id, new Set());
                        tagMap.get(row.photo_id)!.add(row.tag_id);
                        if (row.tags) tagIndex.set(row.tags.id, row.tags);
                    }
                }

                if (tagIndex.size > 0) {
                    setPhotoTagsMap(tagMap);
                    setAvailableTags(Array.from(tagIndex.values()).sort((a, b) => a.name.localeCompare(b.name)));
                }
            }
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching gallery:', err);
        } finally {
            // Only set loading false if not aborted (or just always set it, if we're unmounting it doesn't matter)
            setLoading(false);
        }
    }

    async function loadMorePhotos() {
        if (loadingMore || !hasMore) return;
        const ids = libraryIdsRef.current;
        if (!ids || ids.length === 0) return;
        setLoadingMore(true);
        try {
            const from = offsetRef.current;
            const to = from + GALLERY_PAGE_SIZE - 1;
            const { data, error } = await supabase
                .from('photos')
                .select('*')
                .in('library_id', ids)
                .order('created_at', { ascending: false })
                .order('title', { ascending: false })
                .range(from, to);
            if (error) throw error;

            const rows = (data || []) as any as Photo[];
            offsetRef.current = from + rows.length;
            setHasMore(rows.length === GALLERY_PAGE_SIZE);

            // Skip any rows already present (mirrors the initial dedupe)
            const existing = new Set(photos.map(p => p.google_drive_file_id || p.id));
            const newOnes = rows.filter(p => {
                const key = p.google_drive_file_id || p.id;
                if (existing.has(key)) return false;
                existing.add(key);
                return true;
            });
            if (newOnes.length === 0) return;
            setPhotos(prev => [...prev, ...newOnes]);

            // Load tags for just the newly fetched photos (keeps egress bounded)
            const newIds = newOnes.map(p => p.id);
            const nextTagMap = new Map(photoTagsMap);
            const tagIndex = new Map(availableTags.map(t => [t.id, t] as const));
            const chunkSize = 200;
            for (let i = 0; i < newIds.length; i += chunkSize) {
                const chunk = newIds.slice(i, i + chunkSize);
                const { data: ptData } = await supabase
                    .from('photo_tags')
                    .select('photo_id, tag_id, tags(id, name, type)')
                    .in('photo_id', chunk);
                for (const row of (ptData || []) as any[]) {
                    if (!nextTagMap.has(row.photo_id)) nextTagMap.set(row.photo_id, new Set());
                    nextTagMap.get(row.photo_id)!.add(row.tag_id);
                    if (row.tags) tagIndex.set(row.tags.id, row.tags);
                }
            }
            setPhotoTagsMap(nextTagMap);
            setAvailableTags(Array.from(tagIndex.values()).sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            console.error('Error loading more photos:', err);
        } finally {
            setLoadingMore(false);
        }
    }

    const handleToggleSelect = (photo: Photo) => {
        wholeAlbumRef.current = false; // picking by hand is no longer "the whole album"
        setSelectedPhotos(prev => {
            const isSelected = prev.find(p => p.id === photo.id);
            if (isSelected) {
                return prev.filter(p => p.id !== photo.id);
            }
            return [...prev, photo];
        });
    };

    const handleToggleGroup = (groupPhotos: Photo[], isCurrentlyAllSelected: boolean) => {
        if (isCurrentlyAllSelected) {
            // Remove this group from selection
            setSelectedPhotos(prev => prev.filter(p => !groupPhotos.find(gp => gp.id === p.id)));
        } else {
            // Add missing items from this group
            setSelectedPhotos(prev => {
                const newPhotos = [...prev];
                groupPhotos.forEach(p => {
                    if (!newPhotos.find(np => np.id === p.id)) {
                        newPhotos.push(p);
                    }
                });
                return newPhotos;
            });
        }
    };

    // Compute the order total from the selected photos using the gallery's
    // per-photo price + bundle tiers (mirrors the server-side math).
    const computeTotal = (count: number): number => {
        if (count <= 0 || !library) return 0;
        // À la carte: the same function the server checkout charges with.
        if (galleryRole?.role === 'buyer' && galleryRole.pricing) {
            return buyerTotal(wholeAlbumRef.current ? galleryRole.pricing.albumCount : count, galleryRole.pricing);
        }
        // Everyone else: the same function the server checkout charges with.
        return galleryTotal(wholeAlbumRef.current && albumOffer ? albumOffer.count : count, {
            single: Number(library.price) || 0,
            options: pricingOptions,
            albumPrice: albumOffer?.price ?? null,
            albumCount: albumOffer?.count ?? 0,
        });
    };

    // Same-origin anchor with the stream endpoint's attachment disposition —
    // not window.open, which popup blockers eat when fired in a loop.
    const triggerFileDownload = (photo: Photo, email: string) => {
        markDownloaded([photo.id]);
        const fileId = photo.google_drive_file_id || photo.id;
        const a = document.createElement('a');
        a.href = withGalleryPass(`/api/gallery/stream?fileId=${encodeURIComponent(fileId)}&download=true&email=${encodeURIComponent(email)}`);
        a.download = '';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const downloadPhotos = async (toDownload: Photo[]) => {
        if (toDownload.length === 0) return;
        const email = await requestDownloadEmail(toDownload);
        if (!email) return; // user cancelled
        setBuyerEmail(email);
        // Phones: straight into Photos via the share sheet, not into Files one
        // download at a time (Safari blocks a burst of downloads anyway).
        if (canSaveToPhotos()) {
            setSaveSheet(toDownload.map(photo => {
                const fileId = photo.google_drive_file_id || photo.id;
                return {
                    url: withGalleryPass(`/api/gallery/stream?fileId=${encodeURIComponent(fileId)}&download=true&email=${encodeURIComponent(email)}`),
                    filename: photoFilename(photo.title, photo.mime_type, photo.id),
                    type: photo.mime_type || 'image/jpeg',
                    photoId: photo.id,
                };
            }));
            return;
        }
        if (toDownload.length === 1) {
            triggerFileDownload(toDownload[0], email);
            return;
        }
        setCheckoutPending(true);
        try {
            // Spaced out so the browser treats each as its own download rather
            // than collapsing a burst into one.
            for (let i = 0; i < toDownload.length; i++) {
                triggerFileDownload(toDownload[i], email);
                if (i < toDownload.length - 1) await new Promise(r => setTimeout(r, 1200));
            }
        } finally {
            setCheckoutPending(false);
        }
    };

    const handleCheckout = async (opts?: { wholeAlbum?: boolean }) => {
        // BUY WHOLE ALBUM calls this in the same tap that selects every photo,
        // before the new selection has rendered — so it can't wait on it.
        const wholeAlbum = opts?.wholeAlbum === true;
        wholeAlbumRef.current = wholeAlbum;
        if (!wholeAlbum && selectedPhotos.length === 0) return;
        // A zero-priced gallery has nothing to pay for — the server checkout
        // rejects it outright — so the tray hands out the files directly.
        if (isFreeGallery) {
            await downloadPhotos(selectedPhotos);
            return;
        }
        // A signed-in à la carte buyer pays as themselves (the server uses
        // their session address anyway); no need to type it.
        let email = buyerEmail || (galleryRole?.role === 'buyer' ? user?.email ?? null : null);
        if (!email) {
            email = await requestDownloadEmail(selectedPhotos);
            if (!email) return; // user cancelled
        }
        setBuyerEmail(email);
        setPaymentPickerOpen(true);
    };

    const submitPayment = async (paymentMethod: 'stripe' | 'strike') => {
        const email = buyerEmail;
        if (!email || selectedPhotos.length === 0) return;

        setCheckoutPending(true);
        try {
            const r = await fetch('/api/photos/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    photoIds: selectedPhotos.map(p => p.id),
                    email,
                    paymentMethod,
                    wholeAlbum: wholeAlbumRef.current || undefined,
                    returnPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
                }),
            });
            const d = await r.json();

            if (!r.ok) {
                alert('Checkout failed: ' + (d.error || 'Unknown error'));
                return;
            }

            if (paymentMethod === 'stripe' && d.url) {
                window.location.href = d.url;
                return;
            }

            if (paymentMethod === 'strike' && d.lnInvoice) {
                setPaymentPickerOpen(false);
                setLightningInvoice(d.lnInvoice);
                setLightningAmount(d.amount);
                setLightningModalOpen(true);
            }
        } catch {
            alert('Checkout failed. Please try again.');
        } finally {
            setCheckoutPending(false);
        }
    };

    const [downloadEmailModalOpen, setDownloadEmailModalOpen] = useState(false);
    const [pendingDownloadPhotos, setPendingDownloadPhotos] = useState<Photo[] | null>(null);

    const requestDownloadEmail = (photos: Photo[]): Promise<string | null> => {
        return new Promise((resolve) => {
            const cached = typeof window !== 'undefined'
                ? (localStorage.getItem('tlau_download_email') || localStorage.getItem('credit_email'))
                : null;
            if (cached && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cached)) {
                resolve(cached);
                return;
            }
            setPendingDownloadPhotos(photos);
            setDownloadEmailModalOpen(true);
            const onSubmit = (email: string) => {
                localStorage.setItem('tlau_download_email', email);
                localStorage.setItem('credit_email', email);
                setDownloadEmailModalOpen(false);
                (window as any).__tlauDownloadEmailResolver = null;
                resolve(email);
            };
            const onClose = () => {
                setDownloadEmailModalOpen(false);
                (window as any).__tlauDownloadEmailResolver = null;
                resolve(null);
            };
            (window as any).__tlauDownloadEmailResolver = { onSubmit, onClose };
        });
    };

    // ── Derived values — must be computed before any early returns (Rules of Hooks) ──
    // Same precedence as the server checkout and the header price display:
    // a 'photo_count = 1' pricing option (set in admin) wins over the legacy
    // photo_libraries.price column. Keeps the lightbox + grid + tray in sync.
    const singlePrice = (() => {
        if (galleryRole?.role === 'buyer') return galleryRole.buyerPrice || 5;
        const single = pricingOptions.find(o => o.photo_count === 1);
        if (single) return Number(single.price) || 0;
        return library ? Number(library.price) || 0 : 0;
    })();

    // No per-photo price and no paid bundle: the gallery is a free download.
    // The all-public aggregate view carries a placeholder price of 0, so it
    // is excluded — those photos belong to galleries with their own prices.
    const isFreeGallery = galleryRole?.role === 'owner'
        || (!!library && !isAllPublic && galleryRole?.role !== 'buyer' && singlePrice <= 0
            && !pricingOptions.some(o => Number(o.price) > 0));

    // ── SEO ────────────────────────────────────────────────────────────────────
    // useMemo must be called unconditionally before any early returns.
    const seoMeta = useMemo(() => {
        if (!library || library.slug === 'all-public' || inline) return null;

        const libName  = library.name;
        const city = photos.find(p => p.metadata?.city)?.metadata?.city || 'Austin, TX';
        const cityShort = city.split(',')[0].trim();

        const coverPhoto  = photos[0];
        const coverUrl    = coverPhoto?.google_drive_file_id
            ? `https://www.thelostandunfounds.com/api/gallery/stream?fileId=${encodeURIComponent(coverPhoto.google_drive_file_id)}&size=1200`
            : 'https://www.thelostandunfounds.com/og-image.png';

        const pageTitle   = `${libName} — ${cityShort} Photography | The Lost+Unfounds`;
        const pageDesc    = `${libName} — ${cityShort} photography by The Lost+Unfounds. `
            + `High-resolution photos available for licensing and download. `
            + `Available for event, nightlife, and portrait bookings in ${cityShort} and beyond.`;
        const canonicalUrl = `https://www.thelostandunfounds.com/gallery/${library.slug}`;

        const imageObjects = photos.slice(0, 10).map(p => ({
            '@type': 'ImageObject',
            name: (p.title || libName).replace(/\.[^.]+$/, ''),
            contentUrl: `https://www.thelostandunfounds.com/api/gallery/stream?fileId=${encodeURIComponent(p.google_drive_file_id || '')}&size=1200`,
            thumbnailUrl: `https://www.thelostandunfounds.com/api/gallery/stream?fileId=${encodeURIComponent(p.google_drive_file_id || '')}&size=400`,
            creator: {
                '@type': 'Person',
                name: 'Joshua Abram Greene',
                url: 'https://www.thelostandunfounds.com',
            },
            copyrightHolder: {
                '@type': 'Organization',
                name: 'The Lost+Unfounds',
                url: 'https://www.thelostandunfounds.com',
            },
            locationCreated: {
                '@type': 'Place',
                name: p.metadata?.city || city,
            },
            dateCreated: p.metadata?.date_taken
                ? new Date(p.metadata.date_taken).toISOString().slice(0, 10)
                : undefined,
            ...(singlePrice > 0 && {
                offers: {
                    '@type': 'Offer',
                    price: singlePrice.toFixed(2),
                    priceCurrency: 'USD',
                    availability: 'https://schema.org/InStock',
                    url: canonicalUrl,
                },
            }),
        }));

        const structuredData = {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'CollectionPage',
                    name: pageTitle,
                    description: pageDesc,
                    url: canonicalUrl,
                    image: coverUrl,
                    author: {
                        '@type': 'Person',
                        name: 'Joshua Abram Greene',
                        url: 'https://www.thelostandunfounds.com',
                    },
                    hasPart: imageObjects,
                },
                {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'Home',    item: 'https://www.thelostandunfounds.com/' },
                        { '@type': 'ListItem', position: 2, name: 'Gallery', item: 'https://www.thelostandunfounds.com/gallery' },
                        { '@type': 'ListItem', position: 3, name: libName,   item: canonicalUrl },
                    ],
                },
            ],
        };

        return { pageTitle, pageDesc, coverUrl, canonicalUrl, structuredData };
    }, [library, photos, inline, singlePrice]);

    // ── Early returns (after all hooks) ────────────────────────────────────────
    if (loading) {
        // Inline mode: show a skeleton that doesn't cover the whole screen.
        // Full-page mode: show the standard full-screen overlay.
        if (inline) {
            return (
                <div className="min-h-screen bg-black pt-0 pb-40 px-4 md:px-8 animate-pulse">
                    <div className="max-w-7xl mx-auto pt-12">
                        <div className="h-12 w-64 bg-white/5 mb-4" />
                        <div className="h-4 w-96 bg-white/5 mb-12" />
                        <div className="grid grid-cols-3 gap-1 md:gap-2">
                            {Array.from({ length: 9 }).map((_, i) => (
                                <div key={i} className="aspect-square bg-white/5" />
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        return <Loading />;
    }

    // Filter photos based on date range and/or tag selection
    const filteredPhotos = (activeTab === 'storefront' ? photos : purchasedPhotos).filter(photo => {
        // Date filter
        if (startDate || endDate) {
            const photoDateStr = photo.metadata?.date_taken || photo.metadata?.time || photo.created_at;
            const photoDate = new Date(photoDateStr).getTime();
            if (startDate && photoDate < new Date(startDate).getTime()) return false;
            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (photoDate > end.getTime()) return false;
            }
        }

        // Tag filter (union: photo must have at least one of the selected tags)
        if (selectedTagIds.length > 0) {
            const photoTags = photoTagsMap.get(photo.id);
            if (!photoTags) return false;
            if (!selectedTagIds.some(id => photoTags.has(id))) return false;
        }

        return true;
    });

    const displayedPhotos = filteredPhotos;

    return (
        <div
            className={`min-h-screen bg-black ${inline ? 'pt-0' : 'pt-20'} pb-40 px-4 md:px-8`}
            onContextMenu={(e) => e.preventDefault()}
        >
            {seoMeta && (
                <Helmet>
                    <title>{seoMeta.pageTitle}</title>
                    <meta name="description" content={seoMeta.pageDesc} />
                    <link rel="canonical" href={seoMeta.canonicalUrl} />
                    {/* Open Graph */}
                    <meta property="og:type"        content="website" />
                    <meta property="og:title"       content={seoMeta.pageTitle} />
                    <meta property="og:description" content={seoMeta.pageDesc} />
                    <meta property="og:image"       content={seoMeta.coverUrl} />
                    <meta property="og:url"         content={seoMeta.canonicalUrl} />
                    <meta property="og:site_name"   content="The Lost+Unfounds" />
                    {/* Twitter */}
                    <meta name="twitter:card"        content="summary_large_image" />
                    <meta name="twitter:title"       content={seoMeta.pageTitle} />
                    <meta name="twitter:description" content={seoMeta.pageDesc} />
                    <meta name="twitter:image"       content={seoMeta.coverUrl} />
                    <meta name="twitter:site"        content="@tlau.media" />
                    {/* Structured data */}
                    <script type="application/ld+json">
                        {JSON.stringify(seoMeta.structuredData)}
                    </script>
                </Helmet>
            )}
            {/* Header / Info Section */}
            <div className="max-w-7xl mx-auto mb-6">
                {/* Title Section */}
                <div className="mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-6xl font-black text-white tracking-tight uppercase mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                        {library?.name}
                    </h1>
                    {library?.live_mode && (
                        <p className="text-green-400 text-[10px] md:text-xs font-black uppercase tracking-[0.25em] mb-2">
                            ● Live — new photos appear as they're published
                        </p>
                    )}
                    <p className="text-zinc-500 text-sm md:text-base font-medium tracking-tight mb-2">
                        {library?.description}
                    </p>
                    {library?.metadata?.photographer && (
                        <p className="text-zinc-500 text-sm md:text-base font-medium tracking-tight">
                            Photographed by{' '}
                            {library.metadata.photographer_instagram ? (
                                <a
                                    href={library.metadata.photographer_instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-white/80 underline underline-offset-4 hover:text-white transition-colors"
                                >
                                    {library.metadata.photographer}
                                    {library.metadata.photographer_handle
                                        ? ` (${library.metadata.photographer_handle})`
                                        : ''}
                                </a>
                            ) : (
                                <span className="text-white/80">
                                    {library.metadata.photographer}
                                    {library.metadata.photographer_handle
                                        ? ` (${library.metadata.photographer_handle})`
                                        : ''}
                                </span>
                            )}
                        </p>
                    )}
                    <div className="mb-8" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-32">
                    {/* Left Column: Pricing — single-photo price comes from the
                        'photo_count=1' pricing option if present, else falls back
                        to library.price. Server-side checkout uses the same
                        precedence, so this matches the tray total. */}
                    {!isAllPublic && library && (() => {
                        const singleOption = pricingOptions.find(o => o.photo_count === 1);
                        const headlinePrice = singleOption
                            ? Number(singleOption.price)
                            : Number(library.price);
                        if (isFreeGallery) {
                            return (
                                <div className="space-y-3 max-w-sm">
                                    <div className="inline-flex items-baseline gap-3">
                                        <span className="text-4xl md:text-5xl font-black text-green-400 tracking-tighter leading-none">
                                            FREE
                                        </span>
                                        <span className="text-[9px] font-black tracking-[0.3em] uppercase text-white/50">
                                            Download
                                        </span>
                                    </div>
                                    <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase leading-relaxed pt-2">
                                        No checkout — download straight from the page
                                    </p>
                                </div>
                            );
                        }
                        if (!headlinePrice || headlinePrice <= 0) return null;
                        const bundles = pricingOptions
                            .filter(o => o.photo_count > 1)
                            .sort((a, b) => a.photo_count - b.photo_count);
                        return (
                            <div className="space-y-3 max-w-sm">
                                <div className="inline-flex items-baseline gap-3">
                                    {/* Money is green-400 across the platform (admin revenue
                                        tiles, CHART_ACCENTS revenue). The headline price and the
                                        tray total are the same number, so they match. */}
                                    <span className="text-4xl md:text-5xl font-black text-green-400 tracking-tighter leading-none">
                                        ${headlinePrice.toFixed(0)}
                                    </span>
                                    <span className="text-[9px] font-black tracking-[0.3em] uppercase text-white/50">
                                        Per Photo
                                    </span>
                                </div>
                                {bundles.length > 0 && (
                                    <div className="space-y-1 pt-1">
                                        {bundles.map(opt => (
                                            <p
                                                key={opt.id}
                                                className="text-white/40 text-[10px] font-bold tracking-widest uppercase leading-relaxed"
                                            >
                                                {opt.photo_count} for ${Number(opt.price).toFixed(0)}
                                                <span className="text-white/20 ml-2">
                                                    (${(Number(opt.price) / opt.photo_count).toFixed(2)} ea)
                                                </span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                                {albumOffer ? (
                                    <div className="pt-2 space-y-2">
                                        <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase leading-relaxed">
                                            Whole album ({albumOffer.count}) for ${albumOffer.price.toFixed(0)}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                // Every photo; the server prices it from the database.
                                                setSelectedPhotos(photos);
                                                void handleCheckout({ wholeAlbum: true });
                                            }}
                                            className="px-5 py-3 bg-white text-black text-[10px] font-black uppercase tracking-[0.2em] hover:bg-white/90 transition-colors"
                                            data-tour="buy-whole-album"
                                        >
                                            Buy Whole Album — ${albumOffer.price.toFixed(0)}
                                        </button>
                                    </div>
                                ) : null}
                                <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase leading-relaxed pt-2">
                                    Pay with Card or Bitcoin
                                </p>
                            </div>
                        );
                    })()}

                    {/* Right Column: Instructions */}
                    <div className="space-y-4 max-w-sm flex flex-col justify-start pt-2">
                        <div className="flex items-start gap-4 text-left">
                            <span className="text-[10px] font-black text-white/20 w-4 pt-0.5">01</span>
                            <p className="text-[9px] font-black tracking-[0.2em] uppercase text-white/40 leading-relaxed">
                                Select photos using the checkboxes.
                            </p>
                        </div>
                        <div className="flex items-start gap-4 text-left">
                            <span className="text-[10px] font-black text-white/20 w-4 pt-0.5">02</span>
                            <p className="text-[9px] font-black tracking-[0.2em] uppercase text-white/40 leading-relaxed">
                                {isFreeGallery
                                    ? 'Click "Download" in the tray below.'
                                    : 'Click "Checkout" in the tray below.'}
                            </p>
                        </div>
                        <div className="flex items-start gap-4 text-left">
                            <span className="text-[10px] font-black text-white/20 w-4 pt-0.5">03</span>
                            <p className="text-[9px] font-black tracking-[0.2em] uppercase text-white/40 leading-relaxed">
                                {isFreeGallery
                                    ? 'Or open any photo and download it directly.'
                                    : 'Pick Card or Bitcoin and complete payment.'}
                            </p>
                        </div>
                        <div className="pt-3 mt-2">
                            <p className="text-[8px] font-black tracking-[0.25em] uppercase text-white/30 leading-relaxed">
                                Personal use only
                            </p>
                            <p className="text-[9px] text-white/30 leading-relaxed mt-1">
                                Purchased downloads are licensed for personal use — social media, wallpapers, personal printing.{' '}
                                <a href="/contact" className="text-white/50 hover:text-white underline underline-offset-2 transition-colors">
                                    Commercial licensing →
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Toolbar — tabs only. Every browsing tool now lives in the floating
                console tray at the bottom of the viewport (see below). */}
            {user && (
            <div
                ref={toolbarRef}
                className="sticky z-[100] bg-black w-full py-3 mb-6"
                // 1px under the header so iOS can't round a seam open between them
                style={{ top: 'calc(var(--nav-height, 64px) - 1px)' }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-4">
                    {/* Row 1: Tabs */}
                    <div className="flex gap-8 text-center justify-center w-full relative z-[101]">
                            <button
                                onClick={() => setActiveTab('storefront')}
                                className={`text-[10px] sm:text-xs font-black tracking-[0.2em] uppercase transition-colors relative pb-2 ${activeTab === 'storefront' ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                            >
                                Storefront
                                {activeTab === 'storefront' && (
                                    <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('assets')}
                                className={`text-[10px] sm:text-xs font-black tracking-[0.2em] uppercase transition-colors relative pb-2 ${activeTab === 'assets' ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                            >
                                Your Assets
                                {activeTab === 'assets' && (
                                    <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                                )}
                        </button>
                    </div>
                </div>
            </div>
            )}

            {/* Map View */}
            {viewMode === 'map' && (
                <div className="w-full" style={{ height: 'calc(100vh - 200px)' }}>
                    <PhotoMap
                        className="w-full h-full"
                        onPhotoClick={(mapPhoto) => {
                            const idx = photos.findIndex(p => p.id === mapPhoto.id);
                            if (idx !== -1) {
                                setActivePhotoIndex(idx);
                            } else {
                                setMapActivePhoto({
                                    id: mapPhoto.id,
                                    title: mapPhoto.title,
                                    google_drive_file_id: mapPhoto.google_drive_file_id || '',
                                    thumbnail_url: mapPhoto.thumbnail_url || '',
                                    created_at: '',
                                    library_id: mapPhoto.library_id,
                                    metadata: mapPhoto.metadata,
                                });
                            }
                        }}
                    />
                </div>
            )}

            <div className={`max-w-7xl mx-auto ${viewMode === 'map' ? 'hidden' : ''}`}>

                {/* Photos Grouped by Date */}
                <div ref={photosRef} className="space-y-16">
                    {displayedPhotos.length === 0 ? (
                        <div className="py-40 text-center space-y-6">
                            {library?.is_private && !user ? (
                                <>
                                    <LockClosedIcon className="w-12 h-12 mx-auto text-white/20" />
                                    <p className="text-zinc-500 uppercase tracking-widest font-bold">
                                        This is a private gallery.
                                    </p>
                                    <p className="text-white/40 text-sm max-w-md mx-auto">
                                        Invited? Get a sign-in link by email — no password needed.
                                    </p>
                                    <a
                                        href={`/gallery/${encodeURIComponent(librarySlug)}/access`}
                                        className="inline-block mt-4 px-8 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-white/80 transition-colors"
                                    >
                                        Get My Gallery Link
                                    </a>
                                    <button
                                        onClick={() => {
                                            setAuthTitle(undefined);
                                            setAuthMessage(undefined);
                                            setAuthModalOpen(true);
                                        }}
                                        className="block mx-auto mt-3 text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-widest"
                                    >
                                        Owner? Log in
                                    </button>
                                </>
                            ) : driveAccessDenied ? (
                                <>
                                    <LockClosedIcon className="w-12 h-12 mx-auto text-white/20" />
                                    <p className="text-zinc-500 uppercase tracking-widest font-bold">
                                        Access denied.
                                    </p>
                                    <p className="text-white/40 text-sm max-w-md mx-auto">
                                        You don't have permission to view this private gallery. Contact the photographer to request access.
                                    </p>
                                </>
                            ) : activeTab === 'assets' ? (
                                <>
                                    <p className="text-zinc-500 uppercase tracking-widest font-bold">No purchased assets yet.</p>
                                    <p className="text-white/40 text-sm">Select photos from the Storefront and complete checkout to see them here.</p>
                                </>
                            ) : (
                                <p className="text-zinc-500 uppercase tracking-widest font-bold">No items found in this vault section.</p>
                            )}
                        </div>
                    ) : (
                        Object.entries(
                            displayedPhotos
                                .sort((a, b) => {
                                    // Treat the database timestamp as Local Time (ignore Z/Timezone shift)
                                    // If DB says "05:06:00Z", we want "05:06:00 Local"
                                    const getTimeAsLocal = (dateStr: string) => {
                                        const d = new Date(dateStr);
                                        if (!isNaN(d.getTime())) {
                                            // Construct a new date using the UTC components as Local components
                                            return new Date(
                                                d.getUTCFullYear(),
                                                d.getUTCMonth(),
                                                d.getUTCDate(),
                                                d.getUTCHours(),
                                                d.getUTCMinutes(),
                                                d.getUTCSeconds()
                                            ).getTime();
                                        }
                                        return d.getTime();
                                    };

                                    // Priority: Date Taken (EXIF) > Time > Created At
                                    const dateStrA = a.metadata?.date_taken || a.metadata?.time || a.created_at;
                                    const dateStrB = b.metadata?.date_taken || b.metadata?.time || b.created_at;

                                    return getTimeAsLocal(dateStrB) - getTimeAsLocal(dateStrA);
                                })
                                .reduce((acc, photo) => {
                                    const dateStr = photo.metadata?.date_taken || photo.metadata?.time || photo.created_at;

                                    // Local Time Conversion for Display
                                    let date = new Date(dateStr);
                                    if (!isNaN(date.getTime())) {
                                        date = new Date(
                                            date.getUTCFullYear(),
                                            date.getUTCMonth(),
                                            date.getUTCDate(),
                                            date.getUTCHours(),
                                            date.getUTCMinutes(),
                                            date.getUTCSeconds()
                                        );
                                    }

                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                                    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
                                    const dayNum = date.getDate();
                                    const year = date.getFullYear();

                                    let suffix = 'TH';
                                    if (dayNum % 10 === 1 && dayNum !== 11) suffix = 'ST';
                                    if (dayNum % 10 === 2 && dayNum !== 12) suffix = 'ND';
                                    if (dayNum % 10 === 3 && dayNum !== 13) suffix = 'RD';

                                    const dateString = `${dayName} ${monthName} ${dayNum}${suffix}, ${year}`;

                                    if (!acc[dateString]) acc[dateString] = [];
                                    acc[dateString].push(photo);
                                    return acc;
                                }, {} as Record<string, Photo[]>)
                        ).map(([dateHeader, groupPhotos]) => {
                            const isGroupAllSelected = groupPhotos.every(gp => selectedPhotos.find(p => p.id === gp.id));

                            return (
                                <div key={dateHeader}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex flex-col items-start">
                                            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight whitespace-nowrap">
                                                {dateHeader}
                                            </h2>
                                            {/* Camera Model Display (Group Level - optional fallback) */}
                                            {(() => {
                                                const modelPhoto = groupPhotos.find(p => p.metadata?.cameraModel);
                                                if (modelPhoto?.metadata?.cameraModel) {
                                                    return (
                                                        <span className="text-[10px] md:text-xs font-bold text-white/50 tracking-widest uppercase mt-1">
                                                            {modelPhoto.metadata.cameraMake ? `${modelPhoto.metadata.cameraMake} ` : ''}{modelPhoto.metadata.cameraModel}
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        {activeTab === 'storefront' && (
                                            <button
                                                onClick={() => handleToggleGroup(groupPhotos, isGroupAllSelected)}
                                                className="flex items-center gap-2 px-2 md:px-3 py-1 bg-white/5 text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-colors"
                                                title={isGroupAllSelected ? 'Unselect Group' : 'Select Group'}
                                            >
                                                <QueueListIcon className={`w-3 h-3 ${isGroupAllSelected ? 'text-white' : 'text-white/60'}`} />
                                                <span className="hidden md:inline">{isGroupAllSelected ? 'Unselect Group' : 'Select Group'}</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className={`grid ${viewMode === 'single' ? 'grid-cols-1 gap-4 md:gap-16' : 'grid-cols-3 gap-1 md:gap-2'}`}>
                                        {groupPhotos.map((photo, index) => (
                                            <PhotoCard
                                                key={photo.id}
                                                photo={photo}
                                                index={index}
                                                isSelected={!!selectedPhotos.find(p => p.id === photo.id)}
                                                isPurchased={!!purchasedPhotos.find(p => p.id === photo.id)}
                                                isFree={isFreeGallery}
                                                activeTab={activeTab}
                                                singlePrice={singlePrice}
                                                viewMode={viewMode === 'map' ? 'grid' : viewMode}
                                                onToggleSelect={() => handleToggleSelect(photo)}
                                                onLightbox={() => setActivePhotoIndex(photos.findIndex(p => p.id === photo.id))}
                                                onDownload={() => downloadPhotos([photo])}
                                                isDownloaded={downloadedIds.has(photo.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    {hasMore && activeTab === 'storefront' && displayedPhotos.length > 0 && (
                        <div className="flex justify-center pt-4">
                            <button
                                onClick={loadMorePhotos}
                                disabled={loadingMore}
                                className="px-8 py-3 bg-white/10 text-white font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loadingMore ? 'Loading…' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>
            </div >

            {/* Lightbox */}
            {saveSheet && <SaveToPhotosSheet items={saveSheet} onClose={() => setSaveSheet(null)} onSaved={markDownloaded} />}
            <PhotoLightbox
                photo={activePhotoIndex !== null ? photos[activePhotoIndex] : null}
                onClose={() => setActivePhotoIndex(null)}
                onNext={() => setActivePhotoIndex(prev => prev !== null && (prev < photos.length - 1) ? prev + 1 : 0)}
                onPrev={() => setActivePhotoIndex(prev => prev !== null && (prev > 0) ? prev - 1 : (photos.length - 1))}
                isSelected={activePhotoIndex !== null && !!photos[activePhotoIndex] && !!selectedPhotos.find(p => p.id === photos[activePhotoIndex!].id)}
                isPurchased={activePhotoIndex !== null && !!photos[activePhotoIndex] && !!purchasedPhotos.find(p => p.id === photos[activePhotoIndex!].id)}
                isFree={isFreeGallery}
                onToggleSelect={() => activePhotoIndex !== null && !!photos[activePhotoIndex] && handleToggleSelect(photos[activePhotoIndex!])}
                singlePhotoPrice={singlePrice}
                galleryName={library?.name}
            />

            {/* Map lightbox — for photos clicked on the map that aren't in the current gallery view */}
            <PhotoLightbox
                photo={mapActivePhoto}
                onClose={() => setMapActivePhoto(null)}
                onNext={() => {}}
                onPrev={() => {}}
                isSelected={false}
                isPurchased={false}
                onToggleSelect={() => {}}
                singlePhotoPrice={singlePrice}
                galleryName={library?.name}
            />

            {/* Floating console tray — the customer-facing counterpart to the admin gallery's
                tray in AdminPhotosBrowse.tsx. Frosted glass (bg-white/10 backdrop-blur-md
                rounded-full), fixed so it stays reachable while the grid scrolls underneath.
                It absorbs the old back-to-top button.

                DELIBERATELY NOT bottom-24, which is what Variant B specifies and what the
                back-to-top button used: on a phone that lands a third of the way up the
                screen, well clear of the browser chrome, and it read as floating in the
                middle of the photos. This sits it where a bottom bar belongs. When the
                checkout SelectionTray is up (fixed bottom-0), the tray lifts clear of it.
                See "Platform Console Tray — Variant B" in the bento-design skill.

                The resting offset carries the home-indicator inset, matching the admin tray
                in AdminPhotosBrowse.tsx and the SAGE MODE button that shares this page. The
                flat bottom-6 it replaces did not: index.html sets viewport-fit=cover, so on a
                notched phone with the browser chrome hidden the tray held at 24px and sat
                inside the indicator strip while everything else moved clear of it.

                The lifted state stays flat on purpose. It clears the SelectionTray, which is
                itself fixed bottom-0 without an inset, so its top edge does not move — adding
                env() here would over-lift by the height of the inset. */}
            <div
                className={cn(
                    "fixed left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 transition-[bottom] duration-300",
                    selectedPhotos.length > 0
                        ? "bottom-[8.5rem] md:bottom-28"
                        : "bottom-[calc(1rem+env(safe-area-inset-bottom))]"
                )}
            >
                <AnimatePresence>
                    {openCard && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.15 }}
                            className="bg-black/80 backdrop-blur-2xl w-[calc(100vw-2rem)] max-w-sm p-4 max-h-[50vh] overflow-y-auto"
                        >
                            {openCard === 'filter' && (
                                <>
                                    <p className="text-[9px] uppercase font-black tracking-widest text-white/30 mb-2">Filter by Tag</p>
                                    {availableTags.length === 0 ? (
                                        <p className="text-[10px] text-white/30">No tags in this gallery.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            <button
                                                onClick={() => setSelectedTagIds([])}
                                                className={`px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider transition-colors ${selectedTagIds.length === 0 ? 'bg-white text-black' : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white'}`}
                                            >All</button>
                                            {availableTags.map(tag => {
                                                const active = selectedTagIds.includes(tag.id);
                                                return (
                                                    <button
                                                        key={tag.id}
                                                        onClick={() => setSelectedTagIds(prev =>
                                                            active ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                                                        )}
                                                        className={`px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider transition-colors ${active ? 'bg-white text-black' : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white'}`}
                                                    >{tag.name}</button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex items-center gap-1 p-1.5 bg-white/10 backdrop-blur-md rounded-full">
                    {([
                        {
                            // Far-left, and it cycles grid -> single -> map on tap. No card, no
                            // labels: the icon IS the state, so a view change is one tap and
                            // never a menu to read.
                            id: 'view',
                            icon: viewMode === 'map' ? MapPinIcon : viewMode === 'single' ? StopIcon : Squares2X2Icon,
                            label: 'View',
                            active: viewMode !== 'grid',
                            onClick: () => {
                                setOpenCard(null);
                                setViewMode(m => m === 'grid' ? 'single' : m === 'single' ? 'map' : 'grid');
                            },
                        },
                        {
                            id: 'search',
                            icon: MagnifyingGlassIcon,
                            label: 'Search',
                            active: searchModalOpen,
                            onClick: () => { setOpenCard(null); setSearchModalOpen(true); },
                        },
                        {
                            id: 'filter',
                            icon: FunnelIcon,
                            label: 'Filter',
                            active: openCard === 'filter' || selectedTagIds.length > 0,
                            badge: selectedTagIds.length || undefined,
                            onClick: () => setOpenCard(c => c === 'filter' ? null : 'filter'),
                        },
                        {
                            id: 'dates',
                            icon: CalendarIcon,
                            label: (startDate || endDate) ? 'Clear Dates' : 'Dates',
                            active: !!(startDate || endDate),
                            onClick: () => {
                                setOpenCard(null);
                                if (startDate || endDate) {
                                    setStartDate('');
                                    setEndDate('');
                                    setShowCalendar(false);
                                } else {
                                    setShowCalendar(true);
                                }
                            },
                        },
                        {
                            id: 'cart',
                            icon: CheckCircleIcon,
                            label: 'Selected',
                            active: selectedPhotos.length > 0,
                            badge: selectedPhotos.length || undefined,
                            onClick: () => {
                                setOpenCard(null);
                                if (selectedPhotos.length > 0) handleCheckout();
                            },
                        },
                        {
                            id: 'top',
                            icon: ArrowUpIcon,
                            label: 'Back to Top',
                            active: false,
                            onClick: () => {
                                setOpenCard(null);
                                if (!photosRef.current) {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    return;
                                }
                                // Account for fixed nav + sticky toolbar so the first date group
                                // isn't hidden behind them after scrolling
                                const navH = parseInt(
                                    getComputedStyle(document.documentElement)
                                        .getPropertyValue('--nav-height') || '64'
                                );
                                const toolbarH = toolbarRef.current?.offsetHeight ?? 0;
                                const offset = navH + toolbarH + 8; // 8px breathing room
                                const top =
                                    photosRef.current.getBoundingClientRect().top +
                                    window.scrollY -
                                    offset;
                                window.scrollTo({ top, behavior: 'smooth' });
                            },
                        },
                    ] as const)
                        .filter(btn => !(btn.id === 'top' && !showBackToTop))
                        .map(btn => (
                            <button
                                key={btn.id}
                                onClick={btn.onClick}
                                title={btn.label}
                                aria-label={btn.label}
                                className={`relative p-2.5 sm:p-3 transition-all duration-300 rounded-full group/btn ${
                                    btn.active
                                        ? 'bg-white text-black scale-110'
                                        : 'text-white/60 hover:text-white hover:bg-white/10'
                                }`}
                            >
                                <btn.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                {'badge' in btn && btn.badge ? (
                                    <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-black flex items-center justify-center rounded-full">
                                        {btn.badge}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                </div>
            </div>

            {/* Tray */}
            <SelectionTray
                selectedPhotos={selectedPhotos}
                onRemove={(id) => setSelectedPhotos(prev => prev.filter(p => p.id !== id))}
                onCheckout={handleCheckout}
                loading={checkoutPending}
                totalAmount={computeTotal(selectedPhotos.length)}
                isFree={isFreeGallery}
            />

            {/* Payment method picker — Card vs Bitcoin */}
            <AnimatePresence>
                {paymentPickerOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/90"
                        onClick={() => !checkoutPending && setPaymentPickerOpen(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 16 }}
                            transition={{ duration: 0.2 }}
                            className="relative bg-[#0a0a0a] w-full max-w-sm p-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => !checkoutPending && setPaymentPickerOpen(false)}
                                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
                                aria-label="Close"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-1">
                                Checkout
                            </p>
                            <h2 className="text-xl font-black uppercase tracking-tight text-white mb-1">
                                ${computeTotal(selectedPhotos.length).toFixed(2)}
                            </h2>
                            <p className="text-white/40 text-[11px] leading-relaxed mb-6">
                                {selectedPhotos.length} photo{selectedPhotos.length === 1 ? '' : 's'} from {library?.name}
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => submitPayment('stripe')}
                                    disabled={checkoutPending}
                                    className="w-full py-4 bg-white text-black text-[11px] font-black uppercase tracking-[0.2em] hover:bg-white/90 transition-colors disabled:opacity-50"
                                >
                                    Pay with Card
                                </button>
                                <button
                                    onClick={() => submitPayment('strike')}
                                    disabled={checkoutPending}
                                    className="w-full py-4 bg-white/10 text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-white/20 transition-colors disabled:opacity-50"
                                >
                                    Pay with Bitcoin
                                </button>
                            </div>
                            <p className="text-[9px] text-white/30 leading-relaxed text-center pt-4">
                                Card payments via Stripe. Bitcoin via Lightning.
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lightning payment modal (out-of-credits path) */}
            <AnimatePresence>
                {lightningModalOpen && lightningInvoice && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/90"
                        onClick={() => setLightningModalOpen(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 16 }}
                            transition={{ duration: 0.2 }}
                            className="relative bg-[#0a0a0a] w-full max-w-sm p-8 text-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setLightningModalOpen(false)}
                                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
                                aria-label="Close"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-1">Lightning Payment</p>
                            <p className="text-xl font-black text-white mb-1">${lightningAmount.toFixed(2)}</p>
                            <p className="text-[10px] text-white/40 mb-6">Scan with any Lightning wallet</p>
                            <div className="bg-white p-4 inline-block mb-4">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(lightningInvoice)}`}
                                    alt="Lightning Invoice QR"
                                    className="w-48 h-48"
                                />
                            </div>
                            <p className="text-[9px] text-white/30 font-mono break-all leading-relaxed">
                                {lightningInvoice.slice(0, 40)}...
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AuthModal
                isOpen={authModalOpen}
                onClose={() => {
                    setAuthModalOpen(false);
                }}
                message={authMessage}
                title={authTitle}
                onLoginSuccess={() => {
                    setAuthModalOpen(false);
                }}
            />

            {/* Calendar Modal - Render at root level to prevent clipping */}
            <AnimatePresence>
                {showCalendar && (
                    <NoirDateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(start: string, end: string) => {
                            setStartDate(start);
                            setEndDate(end);
                        }}
                        onClose={() => setShowCalendar(false)}
                    />
                )}
            </AnimatePresence>

            {/* Download Email Modal */}
            <DownloadEmailModal
                isOpen={downloadEmailModalOpen}
                onClose={() => {
                    const r = (window as any).__tlauDownloadEmailResolver;
                    if (r) r.onClose();
                    else setDownloadEmailModalOpen(false);
                }}
                onSubmit={(email) => {
                    const r = (window as any).__tlauDownloadEmailResolver;
                    if (r) r.onSubmit(email);
                    else setDownloadEmailModalOpen(false);
                }}
                photoCount={pendingDownloadPhotos?.length ?? 1}
            />

            {/* Search Modal */}
            <SearchModal
                isOpen={searchModalOpen}
                onClose={() => setSearchModalOpen(false)}
            />
        </div>
    );
};

export default PhotoGallery;
